import { inject, injectable } from "@launchtray/tsyringe-async";
import { GameModel } from "../models/game/GameModel";
import { PlayerController } from "./PlayerController";
import { SceneManager } from "scene-manager";
import { InfoSceneFactory, InjectableFactories, LoseSceneFactory, SettingsSceneFactory } from "../../../composition/InjectableFactories";
import { InjectableInterfaces } from "../../../composition/InjectableInterfaces";
import { IGameDistributionSdk } from "../../../gamedistribution/interfaces/IGameDistributionSdk";

@injectable()
export class GameViewController {
    public constructor(
        private readonly gameModel: GameModel,
        private readonly playerController: PlayerController,
        private readonly sceneManager: SceneManager,
        @inject(InjectableInterfaces.IGameDistributionSdk) private readonly gdSdk: IGameDistributionSdk,
        @inject(InjectableFactories.InfoSceneFactory) private readonly infoSceneFactory: InfoSceneFactory,
        @inject(InjectableFactories.SettingsSceneFactory) private readonly settingsSceneFactory: SettingsSceneFactory,
        @inject(InjectableFactories.LoseSceneFactory) private readonly loseSceneFactory: LoseSceneFactory
    ) { }

    public placeFigure(cellIndex: number): boolean {
        return this.gameModel.placeFigure(cellIndex);
    }

    public selectFigure(index: number): void {
        this.gameModel.selectFigure(index);
    }

    public isSpecialSlotActive(): boolean {
        return this.gameModel.deckModel.IsSpecialSlotActive;
    }

    public showBonusFigure(): void {
        this.playerController.addEraseBooster(-1);
        this.gameModel.deckModel.createSpecialFigure();
    }

    public async showAdForBonusFigure(): Promise<boolean> {
        const result = await this.gdSdk.showRewardedAd();
        if (result) {
            this.playerController.addEraseBooster(1);
        }
        return result;
    }

    public async shuffleDeck(): Promise<void> {
        if (this.playerController.getShuffles() == 0) {
            const result = await this.gdSdk.showRewardedAd();
            if (result) {
                this.playerController.addShuffles(1);
            }
            return;
        }
        this.playerController.reduceShuffles();
        this.gameModel.deckModel.shuffle();
    }

    public removeRandomTriangles(): void {
        this.playerController.addEraseTrianglesBooster(-1);
        this.gameModel.removeRandomTriangles();
    }

    public async watchAdForRemoveRandomTriangles(): Promise<boolean> {
        const result = await this.gdSdk.showRewardedAd();
        if (result) {
            this.playerController.addEraseTrianglesBooster(1);
        }
        return result;
    }

    public async watchMidrollAd(): Promise<void> {
        await this.gdSdk.showMidrollAd();
    }

    public resetGamefield(): void {
        const result = true; //? Watched ad?
        if (!result) {
            this.playerController.resetScore();
            this.playerController.resetShuffles();
        }

        this.gameModel.clearGamefield();
    }

    public setFirstLaunch(): void {
        this.playerController.setFirstLaunch();
    }
    
    public async showInfoPopup(): Promise<void> {
        const infoPopup = await this.infoSceneFactory();
        this.sceneManager.showViewableAsync(infoPopup);
    }

    public async showSettingsPopup(): Promise<void> {
        const settingsPopup = await this.settingsSceneFactory();
        this.sceneManager.showViewableAsync(settingsPopup);
    }

    public async showGameOverScene(): Promise<void> {
        const loseScene = await this.loseSceneFactory();
        this.sceneManager.showViewableAsync(loseScene);
    }
}